// Analytics.js
// ---------------------------------------------------------------------------
// Delivery » Analytics
//
// Six sections, top to bottom:
//   1. Today's activity     — branches x [Outstanding, Completed, Failed,
//                              Collections], live, right now.
//   2. Summary + supporting cards — the KPI strip, per-branch totals, the
//      weekday profile and the average-delivery-time trend, all scoped to
//      the date range picked at the top (unchanged from the previous
//      version of this page).
//   3. Deliveries over time — full width, Completed/Failed/Collections,
//      daily, up to six months back, with its own branch filter.
//   4. Deliveries per hour, by driver, by month — a heatmap, six months.
//   5. Miles vs. hours, by driver, by month — a scatter, six months.
//
// DATA AVAILABILITY — READ BEFORE CHANGING THE TWO FLAGS BELOW.
// `HAS_COLLECTIONS_TREND` and `HAS_DRIVER_HOURS` mark the two places this
// page has to guess at a backend contract that doesn't exist yet:
//
//   COLLECTIONS. `/api/reports/delivery_activity` (the endpoint this whole
//   page runs on) documents its per-day fields as deliveries/failed/
//   recipients/addresses/miles/minutes — nothing about collections. A
//   collection is a distinct order_type elsewhere in this codebase
//   (Customers.js), so it may not even be counted in "deliveries" at all.
//   This page reads `r.collections` defensively in case a future backend
//   change adds it, and shows "not reported yet" rather than a fabricated
//   zero line when it never arrives. Today's Activity grid below has the
//   same problem in miniature — see the comment above aggregateToday().
//
//   DRIVER HOURS. Section 5 asks for "hours per driver" — a clocked-shift
//   figure. Nothing this page can reach returns clocked hours for an
//   arbitrary past month (only a rolling 7-day total, fleet-wide, elsewhere
//   in Head Office). What IS available per driver per day, historically, is
//   `minutes` from delivery_activity — time spent on the road between drops,
//   not time on shift. Sections 4 and 5 use that as a stand-in and label it
//   "driving time" rather than "hours worked" so the chart doesn't quietly
//   claim to be something it isn't. Swap `HAS_DRIVER_HOURS` to true (and
//   point MINUTES_FIELD at the right field) the day a real shift-hours
//   endpoint exists.
// ---------------------------------------------------------------------------
import API_BASE from "./apiBase";
import React, { useEffect, useMemo, useState } from "react";

const API_HOST = `${API_BASE}`;
const HAS_COLLECTIONS_TREND = false; // flip on once the backend returns r.collections
const HAS_DRIVER_HOURS = false;      // flip on once a real clocked-hours-by-month endpoint exists

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function isoDay(d) {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}
function isoToUk(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function ukToIso(uk) {
  const m = String(uk || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = m[1].padStart(2, "0"), mm = m[2].padStart(2, "0"), yyyy = m[3];
  if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return null;
  return `${yyyy}-${mm}-${dd}`;
}
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function dayLabel(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
  const [y, m, d] = iso.split("-").map(Number);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd} ${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}`;
}
function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 27); // last 4 full weeks, inclusive of today
  return { from: isoDay(from), to: isoDay(to) };
}
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthKey(iso) { return iso.slice(0, 7); } // "YYYY-MM"
function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}
/** The six calendar months ending this month, oldest first. */
function lastNMonthKeys(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = n - 1; i >= 0; i--) {
    const t = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}
function monthsAgoIso(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return isoDay(d);
}

// A fixed palette, cycled by index so the same branch/driver keeps the same
// colour across every card that lists it.
const PALETTE = [
  "#0f766e", "#2a78d6", "#ca8a04", "#dc2626", "#7c3aed",
  "#0891b2", "#65a30d", "#c2410c", "#4338ca", "#be185d",
  "#059669", "#b45309",
];

const S = {
  page: { padding: 16 },
  h2: { fontSize: 20, fontWeight: 600, margin: "0 0 12px" },
  h3: { fontSize: 15, fontWeight: 700, margin: "28px 0 10px" },
  controls: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 16 },
  label: { fontSize: 12, color: "#667085", marginBottom: 2, display: "block" },
  field: { padding: "7px 10px", borderRadius: 8, border: "1px solid #d8dee8", fontSize: 14 },
  btn: { padding: "7px 13px", borderRadius: 8, border: "1px solid #d8dee8",
    background: "#fff", color: "#1f2a37", cursor: "pointer", fontSize: 14 },
  btnActive: { background: "#1976d2", color: "#fff", borderColor: "#1976d2" },
  grid: { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "10px 14px 14px", width: 400, maxWidth: "100%", boxSizing: "border-box" },
  cardFull: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "10px 14px 16px", width: "100%", boxSizing: "border-box" },
  cardTitle: { fontSize: 13.5, fontWeight: 800, color: "#111827", marginBottom: 2 },
  cardSub: { fontSize: 11, color: "#6b7280", marginBottom: 10 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 },
  kpiBox: { border: "1px solid #eef2f7", borderRadius: 8, padding: "10px 12px", background: "#fafbfc" },
  kpiVal: { fontSize: 22, fontWeight: 900, color: "#111827", lineHeight: 1.1 },
  kpiLabel: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  empty: { color: "#9ca3af", fontSize: 12.5, padding: "20px 0", textAlign: "center" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb",
    fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" },
  thNum: { textAlign: "right", padding: "6px 8px", borderBottom: "2px solid #e5e7eb",
    fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" },
  td: { padding: "6px 8px", borderBottom: "1px solid #f2f4f7" },
  tdNum: { padding: "6px 8px", borderBottom: "1px solid #f2f4f7", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  note: { fontSize: 11, color: "#9ca3af", marginTop: 8, fontStyle: "italic" },
};

async function apiPost(path, body, token) {
  const res = await fetch(`${API_HOST}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}
/** GET under /api/headoffice/, scoped to the viewer's organisation — mirrors
 *  HeadOffice.js's own apiGet/withOrgScope so this page reads exactly what
 *  the dashboard's "Today's deliveries" snapshot already reads. */
async function apiGetHeadOffice(path, token) {
  const org = localStorage.getItem("organisationName") || "";
  const sep = path.includes("?") ? "&" : "?";
  const url = `${API_HOST}/api/headoffice/${path}${org ? `${sep}organisationName=${encodeURIComponent(org)}` : ""}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Chart primitives
// ---------------------------------------------------------------------------

// DailyBarChart, TrendLine and RankedBarChart are currently unused on this
// page — the cards that called them (Average delivery time, Deliveries by
// day of week, Deliveries/Failed by branch) were removed on request. Left in
// place rather than deleted, since they're generic and cheap to wire back up
// if any of those cards come back.
function DailyBarChart({ dates, values, color = "#0f766e", height = 130, fmt = (v) => v }) {
  const max = Math.max(1, ...values);
  const COL = dates.length > 22 ? Math.max(6, Math.floor(280 / dates.length)) : 12;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: dates.length > 40 ? 1 : 2,
                    height, borderBottom: "1px solid #e5e7eb", overflowX: dates.length > 60 ? "auto" : "visible" }}>
        {dates.map((d, i) => {
          const v = values[i] || 0;
          const h = Math.round((v / max) * (height - 6));
          return (
            <div key={d} title={`${dayLabel(d)}: ${fmt(v)}`}
              style={{ width: COL, minWidth: COL, height: Math.max(h, v > 0 ? 2 : 0),
                       background: color, borderRadius: "2px 2px 0 0" }} />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af", marginTop: 3 }}>
        <span>{dates.length ? dayLabel(dates[0]) : ""}</span>
        <span>{dates.length ? dayLabel(dates[dates.length - 1]) : ""}</span>
      </div>
    </div>
  );
}

function buildPath(values, xFn, yFn) {
  let d = "", started = false;
  values.forEach((v, i) => {
    if (v == null) { started = false; return; }
    d += `${started ? "L" : "M"}${xFn(i)},${yFn(v)} `;
    started = true;
  });
  return d.trim();
}

/** Single-series trend — used for average delivery time. */
function TrendLine({ dates, values, color = "#7c3aed", height = 130, fmt = (v) => v }) {
  const have = values.map((v, i) => (v == null ? null : { i, v })).filter(Boolean);
  if (!have.length) return <div style={S.empty}>No data in this range.</div>;
  const max = Math.max(1, ...have.map((p) => p.v));
  const W = 380;
  const x = (i) => (dates.length > 1 ? (i / (dates.length - 1)) * (W - 16) + 8 : W / 2);
  const y = (v) => height - 8 - (v / max) * (height - 20);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1={0} x2={W} y1={(i / 3) * (height - 20) + 8} y2={(i / 3) * (height - 20) + 8}
            stroke="#f1f5f9" strokeWidth="1" />
        ))}
        <path d={buildPath(values, x, y)} fill="none" stroke={color} strokeWidth="2.2" />
        {have.map((p) => (
          <circle key={p.i} cx={x(p.i)} cy={y(p.v)} r="2.6" fill={color}>
            <title>{`${dayLabel(dates[p.i])}: ${fmt(p.v)}`}</title>
          </circle>
        ))}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af" }}>
        <span>{dates.length ? dayLabel(dates[0]) : ""}</span>
        <span>{dates.length ? dayLabel(dates[dates.length - 1]) : ""}</span>
      </div>
    </div>
  );
}

/** Multi-series trend, full width — Completed / Failed / Collections over up
 *  to six months. No per-point markers (a marker every day across six months
 *  is just noise); month-start gridlines instead of per-day ticks so the
 *  x-axis stays readable at that span. */
function MultiTrendChart({ dates, series, height = 230 }) {
  if (!dates.length) return <div style={S.empty}>No data in this range.</div>;
  const allVals = series.flatMap((s) => s.values).filter((v) => v != null);
  const max = Math.max(1, ...allVals);
  const W = 1200;
  const x = (i) => (dates.length > 1 ? (i / (dates.length - 1)) * (W - 20) + 10 : W / 2);
  const y = (v) => height - 22 - (v / max) * (height - 40);

  // Month-start gridlines: the first date in `dates` for each calendar month.
  const monthTicks = [];
  let lastMonth = null;
  dates.forEach((d, i) => {
    const mk = monthKey(d);
    if (mk !== lastMonth) { monthTicks.push({ i, label: monthLabel(mk).split(" ")[0] }); lastMonth = mk; }
  });

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1={0} x2={W} y1={(i / 3) * (height - 40) + 20} y2={(i / 3) * (height - 40) + 20}
            stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {monthTicks.map((t) => (
          <g key={t.i}>
            <line x1={x(t.i)} x2={x(t.i)} y1={10} y2={height - 22} stroke="#eef2f7" strokeWidth="1" />
            <text x={x(t.i) + 3} y={12} fontSize="9.5" fill="#9ca3af">{t.label}</text>
          </g>
        ))}
        {series.map((s) => (
          <path key={s.name} d={buildPath(s.values, x, y)} fill="none" stroke={s.color} strokeWidth="2" />
        ))}
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
        {series.map((s) => (
          <span key={s.name} style={{ fontSize: 11.5, color: "#374151" }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: s.color,
                           marginRight: 5, borderRadius: 2, verticalAlign: "middle" }} />
            {s.name}
            {s.unavailable && <span style={{ color: "#9ca3af" }}> (not reported yet)</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

function RankedBarChart({ rows, colorOf, fmt = (v) => v }) {
  if (!rows.length) return <div style={S.empty}>No data in this range.</div>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div>
      {rows.slice(0, 10).map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
          <div style={{ width: 110, fontSize: 11.5, color: "#374151", textAlign: "right",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
               title={r.label}>{r.label}</div>
          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 16, position: "relative" }}>
            <div style={{ width: `${(r.value / max) * 100}%`, background: colorOf(r.label),
                          height: "100%", borderRadius: 4, minWidth: r.value > 0 ? 2 : 0 }} />
            <span style={{ position: "absolute", right: 6, top: 0, fontSize: 10.5, fontWeight: 800, color: "#111" }}>
              {fmt(r.value)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Rows x columns heatmap. `valueAt(row,col)` returns a number or null (no
 *  data that cell). Cell colour is scaled to the highest value on the grid. */
function HeatmapGrid({ rowLabels, colLabels, valueAt, fmt = (v) => v, max }) {
  const heat = (v) => {
    if (max <= 0) return { bg: "#f3f4f6", fg: "#111827" };
    const t = Math.max(0, Math.min(1, v / max));
    const c1 = [224, 242, 241], c2 = [15, 118, 110];
    const rgb = c1.map((c, i) => Math.round(c + (c2[i] - c) * t));
    return { bg: `rgb(${rgb.join(",")})`, fg: t > 0.55 ? "#fff" : "#111827" };
  };
  if (!rowLabels.length) return <div style={S.empty}>No drivers with delivery activity in this range.</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
        <thead>
          <tr>
            <th style={{ ...S.th, position: "sticky", left: 0, background: "#fff" }}>Driver</th>
            {colLabels.map((c) => (
              <th key={c} style={{ ...S.thNum, minWidth: 64 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((r) => (
            <tr key={r}>
              <td style={{ ...S.td, fontWeight: 700, whiteSpace: "nowrap", position: "sticky", left: 0, background: "#fff" }}>{r}</td>
              {colLabels.map((c) => {
                const v = valueAt(r, c);
                const { bg, fg } = v == null ? { bg: "#f9fafb", fg: "#c1c8d1" } : heat(v);
                return (
                  <td key={c} title={`${r} · ${c}: ${v == null ? "no deliveries that month" : fmt(v)}`}
                    style={{ textAlign: "center", padding: "8px 6px", background: bg, color: fg,
                             fontWeight: 700, border: "1px solid #fff" }}>
                    {v == null ? "–" : fmt(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Scatter — one point per driver per month. */
function ScatterPlot({ points, height = 340, xLabel, yLabel }) {
  if (!points.length) return <div style={S.empty}>No data in this range.</div>;
  const W = 760;
  const maxX = Math.max(1, ...points.map((p) => p.x));
  const maxY = Math.max(1, ...points.map((p) => p.y));
  const px = (x) => (x / maxX) * (W - 70) + 55;
  const py = (y) => height - 34 - (y / maxY) * (height - 54);
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const yy = height - 34 - (i / 4) * (height - 54);
        return (
          <g key={i}>
            <line x1={55} x2={W - 10} y1={yy} y2={yy} stroke="#f1f5f9" strokeWidth="1" />
            <text x={48} y={yy + 3} fontSize="9.5" fill="#9ca3af" textAnchor="end">
              {Math.round((maxY * i) / 4)}
            </text>
          </g>
        );
      })}
      <line x1={55} x2={55} y1={10} y2={height - 34} stroke="#d1d5db" />
      <line x1={55} x2={W - 10} y1={height - 34} y2={height - 34} stroke="#d1d5db" />
      {points.map((p, i) => (
        <circle key={i} cx={px(p.x)} cy={py(p.y)} r="5.5" fill={p.color} fillOpacity="0.82">
          <title>{p.label}</title>
        </circle>
      ))}
      <text x={W / 2} y={height - 6} fontSize="11" fill="#6b7280" textAnchor="middle">{xLabel}</text>
      <text x={16} y={height / 2} fontSize="11" fill="#6b7280" textAnchor="middle"
            transform={`rotate(-90 16 ${height / 2})`}>{yLabel}</text>
    </svg>
  );
}

function Kpi({ value, label, tone }) {
  return (
    <div style={S.kpiBox}>
      <div style={{ ...S.kpiVal, color: tone || S.kpiVal.color }}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Today's activity
// ---------------------------------------------------------------------------
// SOURCE: the same live snapshot HeadOffice's "Today's deliveries" card reads
// (`GET /api/headoffice/deliveries-today/deliveries`) — one row per customer,
// today, with per-parcel delivered/failed/outstanding counts and a
// `statusKey`. It is NOT one row per parcel and it is NOT scoped to a
// deliberate "collection" flag — order_type (seen on Customers.js) may or may
// not travel with this particular payload. Rows are treated as a collection
// if `order_type`/`orderType` says so; failing that, as a fallback, a row
// sitting at statusKey "collected" (defined in HeadOffice.js as "a collection
// in the driver's van") is counted as one. If neither is present the
// Collections column will under-count — worth confirming against a real
// collection-heavy branch before trusting the number.
function aggregateToday(rows, branchNames) {
  const map = {};
  branchNames.forEach((b) => { map[b] = { outstanding: 0, completed: 0, failed: 0, collections: 0 }; });
  for (const r of rows) {
    const b = r.branchName || "Unknown";
    if (!map[b]) map[b] = { outstanding: 0, completed: 0, failed: 0, collections: 0 };
    const isCollection = r.order_type === "collection" || r.orderType === "collection"
      || ((r.order_type === undefined && r.orderType === undefined) && r.statusKey === "collected");
    if (isCollection) {
      map[b].collections += Number(r.parcels || 1);
    } else {
      map[b].outstanding += Number(r.outstanding || 0);
      map[b].completed += Number(r.delivered || 0);
      map[b].failed += Number(r.failed || 0);
    }
  }
  return map;
}

function TodayActivity({ token, branchNames }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await apiGetHeadOffice("deliveries-today/deliveries", token);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message || "Failed to load"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const byBranch = useMemo(() => aggregateToday(rows || [], branchNames), [rows, branchNames]);
  const branches = Object.keys(byBranch).sort((a, b) => a.localeCompare(b));
  const totals = branches.reduce((acc, b) => {
    acc.outstanding += byBranch[b].outstanding;
    acc.completed += byBranch[b].completed;
    acc.failed += byBranch[b].failed;
    acc.collections += byBranch[b].collections;
    return acc;
  }, { outstanding: 0, completed: 0, failed: 0, collections: 0 });

  return (
    <div style={S.cardFull}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={S.cardTitle}>Today's activity</div>
          <div style={S.cardSub}>Live, right now — every branch you can see.</div>
        </div>
        <div style={{ flex: 1 }} />
        {loading && <span style={{ fontSize: 12, color: "#6b7280" }}>Loading…</span>}
        {error && <span style={{ fontSize: 12, color: "#b91c1c" }}>{error}</span>}
        <button style={S.btn} onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {branches.length === 0 && !loading ? (
        <div style={S.empty}>Nothing recorded for today yet.</div>
      ) : (
        <table style={{ ...S.table, marginTop: 10 }}>
          <thead>
            <tr>
              <th style={S.th}>Branch</th>
              <th style={S.thNum}>Outstanding</th>
              <th style={S.thNum}>Completed</th>
              <th style={S.thNum}>Failed</th>
              <th style={S.thNum}>Collections</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b}>
                <td style={{ ...S.td, fontWeight: 700 }}>{b}</td>
                <td style={S.tdNum}>{byBranch[b].outstanding}</td>
                <td style={S.tdNum}>{byBranch[b].completed}</td>
                <td style={{ ...S.tdNum, color: byBranch[b].failed > 0 ? "#b91c1c" : undefined }}>{byBranch[b].failed}</td>
                <td style={S.tdNum}>{byBranch[b].collections}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...S.td, fontWeight: 800, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>Total</td>
              <td style={{ ...S.tdNum, fontWeight: 800, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{totals.outstanding}</td>
              <td style={{ ...S.tdNum, fontWeight: 800, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{totals.completed}</td>
              <td style={{ ...S.tdNum, fontWeight: 800, borderTop: "2px solid #e5e7eb", borderBottom: "none",
                           color: totals.failed > 0 ? "#b91c1c" : undefined }}>{totals.failed}</td>
              <td style={{ ...S.tdNum, fontWeight: 800, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{totals.collections}</td>
            </tr>
          </tfoot>
        </table>
      )}
      <div style={S.note}>
        "Collections" is best-effort — see the comment above aggregateToday() in Analytics.js if this
        looks wrong for a collection-heavy branch.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Deliveries over time (full width, up to 6 months)
// ---------------------------------------------------------------------------
function DeliveriesOverTime({ token, members }) {
  const [months, setMonths] = useState(6);
  const [branch, setBranch] = useState("ALL");
  const [data, setData] = useState({ dates: [], completed: [], failed: [], collections: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const branchObj = members.find((m) => m.name === branch);
        const accountIds = branch === "ALL" ? [] : (branchObj?.id != null ? [String(branchObj.id)] : []);
        const from = monthsAgoIso(months);
        const to = isoDay(new Date());
        const res = await apiPost("api/reports/delivery_activity", { accountIds, driverId: null, from, to }, token);
        const rows = Array.isArray(res?.days) ? res.days : [];
        if (cancelled) return;
        setData({
          dates: rows.map((r) => r.date),
          completed: rows.map((r) => Number(r.deliveries || 0)),
          failed: rows.map((r) => Number(r.failed || 0)),
          // Defensive read — see HAS_COLLECTIONS_TREND note at the top of this file.
          collections: rows.map((r) => (r.collections != null ? Number(r.collections) : null)),
        });
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, members, branch, months]);

  const collectionsHaveData = HAS_COLLECTIONS_TREND && data.collections.some((v) => v != null && v > 0);

  const series = [
    { name: "Completed", color: "#16a34a", values: data.completed },
    { name: "Failed", color: "#dc2626", values: data.failed },
    {
      name: "Collections", color: "#7c3aed",
      values: collectionsHaveData ? data.collections : data.dates.map(() => null),
      unavailable: !collectionsHaveData,
    },
  ];

  return (
    <div style={S.cardFull}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <div>
          <div style={S.cardTitle}>Deliveries over time</div>
          <div style={S.cardSub}>Completed, failed and collections, daily.</div>
        </div>
        <div style={{ flex: 1 }} />
        <select value={branch} onChange={(e) => setBranch(e.target.value)} style={{ ...S.field, minWidth: 160 }}>
          <option value="ALL">All branches</option>
          {members.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
        {[1, 3, 6].map((n) => (
          <button key={n} onClick={() => setMonths(n)}
            style={{ ...S.btn, ...(months === n ? S.btnActive : {}) }}>
            {n === 1 ? "1 month" : `${n} months`}
          </button>
        ))}
        {loading && <span style={{ fontSize: 12, color: "#6b7280" }}>Loading…</span>}
        {error && <span style={{ fontSize: 12, color: "#b91c1c" }}>{error}</span>}
      </div>
      <MultiTrendChart dates={data.dates} series={series} />
      {!collectionsHaveData && (
        <div style={S.note}>
          Collections aren't returned by the reporting endpoint yet, so that line is flat — see
          HAS_COLLECTIONS_TREND at the top of Analytics.js.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections 4 & 5 — per-driver monthly figures (heatmap + scatter)
// ---------------------------------------------------------------------------
function useDriverMonthly(token, monthsBack = 6) {
  const [byDriver, setByDriver] = useState([]); // [{driverId, driverName, months:{key:{deliveries,minutes,miles}}}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const monthKeys = useMemo(() => lastNMonthKeys(monthsBack), [monthsBack]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const driversRes = await apiPost("api/reports/delivery_activity_drivers", { accountIds: [] }, token);
        const drivers = Array.isArray(driversRes?.drivers) ? driversRes.drivers : [];
        const from = monthsAgoIso(monthsBack);
        const to = isoDay(new Date());
        const results = await Promise.all(drivers.map(async (d) => {
          const months = {};
          try {
            const res = await apiPost("api/reports/delivery_activity",
              { accountIds: [], driverId: String(d.driverId), from, to }, token);
            const rows = Array.isArray(res?.days) ? res.days : [];
            for (const r of rows) {
              const mk = monthKey(r.date);
              months[mk] = months[mk] || { deliveries: 0, minutes: 0, miles: 0 };
              months[mk].deliveries += Number(r.deliveries || 0);
              months[mk].minutes += Number(r.minutes || 0);
              months[mk].miles += Number(r.miles || 0);
            }
          } catch { /* one driver failing shouldn't blank the rest */ }
          return { driverId: d.driverId, driverName: d.driverName || String(d.driverId), months };
        }));
        if (!cancelled) setByDriver(results);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load driver figures");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, monthsBack]);

  return { byDriver, monthKeys, loading, error };
}

function DeliveriesPerHourByDriver({ token }) {
  const { byDriver, monthKeys, loading, error } = useDriverMonthly(token, 6);

  const rowLabels = byDriver.map((d) => d.driverName).sort((a, b) => a.localeCompare(b));
  const byName = Object.fromEntries(byDriver.map((d) => [d.driverName, d]));
  const colLabels = monthKeys.map(monthLabel);
  const keyForLabel = Object.fromEntries(monthKeys.map((k) => [monthLabel(k), k]));

  const valueAt = (name, colLabel) => {
    const mk = keyForLabel[colLabel];
    const m = byName[name]?.months?.[mk];
    if (!m || !m.minutes) return null;
    return Math.round((m.deliveries / (m.minutes / 60)) * 10) / 10;
  };
  const allVals = rowLabels.flatMap((r) => colLabels.map((c) => valueAt(r, c))).filter((v) => v != null);
  const max = Math.max(1, ...allVals);

  return (
    <div style={S.cardFull}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={S.cardTitle}>Deliveries per hour, by driver</div>
          <div style={S.cardSub}>By month, last six months. Darker = more deliveries per hour.</div>
        </div>
        <div style={{ flex: 1 }} />
        {loading && <span style={{ fontSize: 12, color: "#6b7280" }}>Loading — one call per driver, this can take a moment…</span>}
        {error && <span style={{ fontSize: 12, color: "#b91c1c" }}>{error}</span>}
      </div>
      <div style={{ marginTop: 10 }}>
        <HeatmapGrid rowLabels={rowLabels} colLabels={colLabels} valueAt={valueAt} max={max} fmt={(v) => v} />
      </div>
      <div style={S.note}>
        {HAS_DRIVER_HOURS
          ? "Hours are clocked shift time."
          : "\"Per hour\" here means per hour of time on the road between drops (from the reporting endpoint), " +
            "not clocked shift hours — see HAS_DRIVER_HOURS at the top of Analytics.js. Treat this as a first pass."}
      </div>
    </div>
  );
}

function MilesVsHoursByDriver({ token }) {
  const { byDriver, monthKeys, loading, error } = useDriverMonthly(token, 6);

  const colorIndex = useMemo(() => {
    const m = {};
    byDriver.forEach((d, i) => { m[d.driverName] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [byDriver]);

  const points = [];
  byDriver.forEach((d) => {
    monthKeys.forEach((mk) => {
      const m = d.months[mk];
      if (!m || !m.minutes) return;
      const hours = Math.round((m.minutes / 60) * 10) / 10;
      points.push({
        x: hours,
        y: Math.round(m.miles * 10) / 10,
        color: colorIndex[d.driverName],
        label: `${d.driverName} · ${monthLabel(mk)} — ${hours} hrs, ${Math.round(m.miles)} mi`,
      });
    });
  });

  return (
    <div style={S.cardFull}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={S.cardTitle}>Miles vs. hours, by driver</div>
          <div style={S.cardSub}>One point per driver per month, last six months.</div>
        </div>
        <div style={{ flex: 1 }} />
        {loading && <span style={{ fontSize: 12, color: "#6b7280" }}>Loading…</span>}
        {error && <span style={{ fontSize: 12, color: "#b91c1c" }}>{error}</span>}
      </div>
      <div style={{ marginTop: 10, maxWidth: 780 }}>
        <ScatterPlot points={points}
          xLabel={HAS_DRIVER_HOURS ? "Hours worked" : "Hours driving between drops"}
          yLabel="Miles" />
      </div>
      {byDriver.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 8 }}>
          {byDriver.map((d) => (
            <span key={d.driverId} style={{ fontSize: 10.5, color: "#374151" }}>
              <span style={{ display: "inline-block", width: 9, height: 9, background: colorIndex[d.driverName],
                             marginRight: 4, borderRadius: "50%", verticalAlign: "middle" }} />
              {d.driverName}
            </span>
          ))}
        </div>
      )}
      <div style={S.note}>
        {HAS_DRIVER_HOURS
          ? "Hours are clocked shift time."
          : "\"Hours\" is time on the road between drops (from the reporting endpoint), not clocked shift " +
            "hours — see HAS_DRIVER_HOURS at the top of Analytics.js."}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function Analytics({ token }) {
  const members = useMemo(() => {
    try {
      const m = JSON.parse(localStorage.getItem("members") || "[]");
      return (Array.isArray(m) ? m : []).filter((m) => m && m.id != null)
        .slice().sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    } catch { return []; }
  }, []);

  const initRange = defaultRange();
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // branchName -> [{date, deliveries, failed, minutes, miles}]
  const [perBranch, setPerBranch] = useState({});

  const branchNames = members.map((m) => m.name);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        if (!members.length) {
          setPerBranch({});
          return;
        }
        const results = await Promise.all(members.map(async (m) => {
          try {
            const data = await apiPost("api/reports/delivery_activity",
              { accountIds: [String(m.id)], driverId: null, from, to }, token);
            const rows = Array.isArray(data?.days) ? data.days : [];
            return [m.name, rows.map((r) => ({
              date: r.date,
              deliveries: Number(r.deliveries || 0),
              failed: Number(r.failed || 0),
              minutes: Number(r.minutes || 0),
              miles: Number(r.miles || 0),
            }))];
          } catch {
            return [m.name, []];
          }
        }));
        if (cancelled) return;
        setPerBranch(Object.fromEntries(results));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.length, from, to, token]);

  // ---- Derived, feeds only the trimmed Summary card now -------------------
  const grand = useMemo(() => {
    const g = { deliveries: 0, failed: 0, minutes: 0, miles: 0 };
    Object.values(perBranch).forEach((rows) => rows.forEach((r) => {
      g.deliveries += r.deliveries;
      g.failed += r.failed;
      g.minutes += r.minutes;
      g.miles += r.miles;
    }));
    return g;
  }, [perBranch]);

  const totalCalendarDays = useMemo(() => {
    const start = /^\d{4}-\d{2}-\d{2}$/.test(from) ? new Date(`${from}T00:00:00Z`) : null;
    const end = /^\d{4}-\d{2}-\d{2}$/.test(to) ? new Date(`${to}T00:00:00Z`) : null;
    if (!start || !end || start > end) return 1;
    return Math.round((end - start) / 86400000) + 1;
  }, [from, to]);

  const failureRate = grand.deliveries ? Math.round((grand.failed / grand.deliveries) * 1000) / 10 : 0;
  const avgPerDay = Math.round((grand.deliveries / totalCalendarDays) * 10) / 10;
  const avgMinsPerDelivery = grand.deliveries ? Math.round((grand.minutes / grand.deliveries) * 10) / 10 : 0;

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Delivery Analytics</h2>

      {/* ---------------- 1. Today's activity + Summary, side by side ---------------- */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ flex: "1 1 460px", minWidth: 0 }}>
          <TodayActivity token={token} branchNames={branchNames} />
        </div>

        <div style={{ flex: "1 1 460px", minWidth: 0 }}>
          <h3 style={{ ...S.h3, marginTop: 0 }}>Summary for a date range</h3>
          <div style={S.controls}>
            <div>
              <label style={S.label}>From</label>
              <input type="text" placeholder="DD/MM/YYYY" style={{ ...S.field, width: 120 }}
                defaultValue={isoToUk(from)}
                onBlur={(e) => { const iso = ukToIso(e.target.value); if (iso) setFrom(iso); else e.target.value = isoToUk(from); }} />
            </div>
            <div>
              <label style={S.label}>To</label>
              <input type="text" placeholder="DD/MM/YYYY" style={{ ...S.field, width: 120 }}
                defaultValue={isoToUk(to)}
                onBlur={(e) => { const iso = ukToIso(e.target.value); if (iso) setTo(iso); else e.target.value = isoToUk(to); }} />
            </div>
            {[
              ["7", "Last 7 days"], ["28", "Last 4 weeks"], ["90", "Last 90 days"],
            ].map(([n, lbl]) => (
              <button key={n} style={S.btn} onClick={() => {
                const t = new Date(); const f = new Date(); f.setDate(f.getDate() - (Number(n) - 1));
                setFrom(isoDay(f)); setTo(isoDay(t));
              }}>{lbl}</button>
            ))}
            {loading && <span style={{ fontSize: 12.5, color: "#6b7280" }}>Loading…</span>}
            {error && <span style={{ fontSize: 12.5, color: "#b91c1c" }}>{error}</span>}
          </div>

          {!members.length && !loading ? (
            <div style={S.empty}>No branches found for this account.</div>
          ) : (
            <div style={S.cardFull}>
              <div style={S.cardTitle}>Summary</div>
              <div style={S.cardSub}>{dayLabel(from)} – {dayLabel(to)} · every branch you can see</div>
              <div style={S.kpiGrid}>
                <Kpi value={grand.deliveries} label="Parcels delivered" />
                <Kpi value={grand.failed} label="Failed attempts" tone={grand.failed > 0 ? "#b91c1c" : undefined} />
                <Kpi value={`${failureRate}%`} label="Failure rate" tone={failureRate > 5 ? "#b91c1c" : undefined} />
                <Kpi value={avgPerDay} label="Avg parcels / day" />
                <Kpi value={avgMinsPerDelivery ? `${avgMinsPerDelivery} min` : "—"} label="Avg time / delivery" />
                <Kpi value={Math.round(grand.miles)} label="Total miles" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- 2. Deliveries over time, full width ---------------- */}
      <h3 style={S.h3}>Deliveries over time</h3>
      <DeliveriesOverTime token={token} members={members} />

      {/* ---------------- 3. Deliveries per hour, by driver ---------------- */}
      <h3 style={S.h3}>Deliveries per hour, by driver</h3>
      <DeliveriesPerHourByDriver token={token} />

      {/* ---------------- 4. Miles vs. hours, by driver ---------------- */}
      <h3 style={S.h3}>Miles vs. hours, by driver</h3>
      <MilesVsHoursByDriver token={token} />
    </div>
  );
}
